import React, { useEffect, useRef, useState } from 'react';
import '../App.css';
import './Tome.css';

/* ── Intersection Observer hook for scroll-reveal ── */
function useReveal(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold: 0.15, ...options }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);          // eslint-disable-line react-hooks/exhaustive-deps
  return [ref, visible];
}

/* ── Reusable reveal wrapper ── */
function Reveal({ children, className = '', delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div
      ref={ref}
      className={`tome-reveal ${vis ? 'tome-visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ═══════════════════════  PAGE  ═══════════════════════ */

export default function Tome() {
  /* parallax-light: track scroll for the hero gradient shift */
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="App tome-page">

      {/* ── Hero ── */}
      <header
        className="hero tome-hero"
        style={{ backgroundPositionY: scrollY * 0.35 }}
      >
        <Reveal>
          <p className="tome-hero-eyebrow">Introducing</p>
          <h1 className="hero-title tome-hero-title">Tome</h1>
          <p className="hero-subtitle tome-hero-sub">
            Private AI, on your terms. Run powerful large language models entirely
            on your Windows or macOS machine — your data never leaves your device.
          </p>
          <div className="tome-badges">
            <span className="tome-badge">Windows</span>
            <span className="tome-badge">macOS</span>
            <span className="tome-badge tome-badge-accent">100 % Local</span>
          </div>
        </Reveal>
        <div
          className="tome-hero-watermark"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/TomeIcon.png)` }}
        />
        <div className="tome-hero-fade" />
      </header>

      {/* ── The Problem ── */}
      <section className="tome-problem-section">
        <Reveal>
          <h2 className="tome-section-title">The Problem</h2>
        </Reveal>

        <div className="tome-problem-list">
          <Reveal delay={0}>
            <div className="tome-problem-item">
              <h3 className="tome-problem-heading">🌍 Environmental Impact of AI</h3>
              <blockquote className="tome-problem-quote">
                “Amazon’s data centres were projected to use 7.7 billion gallons of water
                a year by 2030, according to the leaked strategy memo, which was circulated
                within the company in 2022.”
              </blockquote>
              <a
                href="https://www.source-material.org/amazon-leak-reveals-true-data-centres-water-usage-secret-plan/?utm_source=chatgpt.com"
                target="_blank"
                rel="noopener noreferrer"
                className="tome-problem-source"
              >
                Source Material →
              </a>
            </div>
          </Reveal>

          <Reveal delay={150}>
            <div className="tome-problem-item">
              <h3 className="tome-problem-heading">🔒 Compromised Privacy in the AI Era</h3>
              <blockquote className="tome-problem-quote">
                “AI systems are so data-hungry and intransparent that we have even less
                control over what information about us is collected, what it is used for,
                and how we might correct or remove such personal information.”
              </blockquote>
              <a
                href="https://hai.stanford.edu/news/privacy-ai-era-how-do-we-protect-our-personal-information"
                target="_blank"
                rel="noopener noreferrer"
                className="tome-problem-source"
              >
                Stanford HAI →
              </a>
            </div>
          </Reveal>

          <Reveal delay={300}>
            <div className="tome-problem-item">
              <h3 className="tome-problem-heading">🚧 Limited Accessibility of Local AI</h3>
              <blockquote className="tome-problem-quote">
                “Commercial foundation models are trained in a paradigm that assumes a
                constant scaling of technologies, which drives both market competition and
                technological power. These models require huge financial investments that can
                only be afforded by companies with monopolistic positions in the digital
                market&hellip; The growth of alternative solutions is increasingly seen as one
                of the ways, alongside regulation, to curb concentrations of power in AI,
                and thus to democratize AI development.”
              </blockquote>
              <a
                href="https://openfuture.eu/wp-content/uploads/2024/11/241128_AI-Speaks-Polish-EN.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="tome-problem-source"
              >
                Open Future →
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Why It Matters ── */}
      <section className="tome-why-section">
        <Reveal>
          <h2 className="tome-section-title">Why It Matters</h2>
          <p className="tome-why-statement">
            AI has become a cornerstone of daily life — appearing in nearly every facet of
            our digital world. It is more important than ever that we inform the public of
            the real risks to <strong>privacy</strong> and the <strong>environment</strong>.
          </p>
          <p className="tome-why-research">
            Our research team has conducted an extensive meta-analysis on
            <strong> Small Language Models</strong> and their viability as a private,
            resource-efficient alternative.
          </p>
          <a
            href="https://www.nicholaslaureano.com/assets/documents/Small_Language_Models_(SLMs)_A_Case-Study_Review_and_Recommendations.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="hero-button tome-why-cta"
          >
            Read the Research Paper →
          </a>
        </Reveal>
      </section>

      {/* ── What Tome Does ── */}
      <section className="tome-features-section">
        <Reveal>
          <h2 className="tome-section-title">What Tome Does</h2>
        </Reveal>

        <div className="tome-feature-list">
          {[
            { icon: '⚡', headline: 'AI that lives on your machine.', sub: 'Run powerful open-source LLMs entirely on-device — no cloud, no latency, no compromise.' },
            { icon: '📎', headline: 'Your documents. Your context.', sub: 'Upload files as sources and get answers grounded in your own data — privately, locally, instantly.' },
            { icon: '🔀', headline: 'Swap models in seconds.', sub: 'Switch inference and embedding models on the fly — match the right tool to the task without breaking your workflow.' },
          ].map((f, i) => (
            <Reveal key={f.headline} delay={i * 150}>
              <div className="tome-feature-item">
                <span className="tome-feature-icon">{f.icon}</span>
                <div>
                  <p className="tome-feature-headline">{f.headline}</p>
                  <p className="tome-feature-sub">{f.sub}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── Built For ── */}
      <section className="tome-audience-section">
        <Reveal>
          <h2 className="tome-section-title">Built For</h2>
        </Reveal>
        <div className="tome-audience-grid">
          {[
            { icon: '🔬', label: 'Researchers' },
            { icon: '🎓', label: 'Students' },
            { icon: '💡', label: 'Entrepreneurs' },
            { icon: '🛡️', label: 'Privacy-Conscious Individuals' },
          ].map((a, i) => (
            <Reveal key={a.label} delay={i * 100}>
              <div className="tome-audience-card">
                <span className="tome-audience-icon">{a.icon}</span>
                <span className="tome-audience-label">{a.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── Download ── */}
      <section className="tome-countdown-section">
        <Reveal>
          <h2 className="tome-section-title">Available on Windows</h2>
          <p className="tome-countdown-sub">
            Download Tome for Windows today.
          </p>
          <a
            href="https://runic-api-server-production.up.railway.app/download-tome-windows"
            className="hero-button"
          >
            Download for Windows
          </a>
          <p className="tome-countdown-sub">Mac support coming soon.</p>
        </Reveal>
      </section>

      {/* ── Android Prototype ── */}
      <section className="services" style={{ paddingTop: '2rem', paddingBottom: '3rem' }}>
        <p style={{ fontSize: '1rem', color: '#a0e7ff', textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
          Check out our <a href="/tomemobile" style={{ color: '#6bd4ff', textDecoration: 'underline' }}>first prototype of Tome on Android</a>.
        </p>
      </section>

      <footer className="footer">
        <p>© 2026 RUNIC Solutions — Tome: AI that stays where it belongs.</p>
      </footer>

    </div>
  );
}
